import type { Car, Game, Track } from "@vms/types";
import { GAME_OPTIONS } from "@vms/types";

interface BuildAutoSubheadingArgs {
  tracks: string[];
  cars: string[];
  game: Game;
  eventIds: string[];
  dateFrom: string;
  dateTo: string;
  allTracks: Track[] | null | undefined;
  allCars: Car[] | null | undefined;
}

/**
 * Build a default subheading string from a leaderboard's active filters.
 * Single track / single car => its name (with layout if present).
 * Multiple => `"N tracks"` / `"N cars"`.
 * Always appends the game label, plus optional event count + date range.
 * Returns "" if neither content lookup table is loaded yet — callers should
 * gate the assignment on a non-empty return value.
 *
 * Pure function, dependency-free. Extracted from hub-ui's
 * LeaderboardManagementForm so cloud-ui can reuse the same heuristic.
 */
export function buildAutoSubheading({
  tracks,
  cars,
  game,
  eventIds,
  dateFrom,
  dateTo,
  allTracks,
  allCars,
}: BuildAutoSubheadingArgs): string {
  if (!allCars || !allTracks) return "";

  const parts: string[] = [];

  if (tracks.length === 1) {
    const [trackId, layoutId] = tracks[0].split("/");
    const track = allTracks.find((t) => t.trackId === trackId);
    const layout = layoutId ? track?.layouts?.find((l) => l.layoutId === layoutId) : null;
    const trackLabel = track?.name ?? trackId;
    parts.push(layout ? `${trackLabel} (${layout.name})` : trackLabel);
  } else if (tracks.length > 1) {
    parts.push(`${tracks.length} tracks`);
  }

  if (cars.length === 1) {
    const car = allCars.find((c) => c.carId === cars[0]);
    parts.push(car?.name ?? cars[0]);
  } else if (cars.length > 1) {
    parts.push(`${cars.length} cars`);
  }

  const gameLabel = GAME_OPTIONS.find((g) => g.value === game)?.label ?? game;
  if (gameLabel) parts.push(String(gameLabel));

  if (eventIds.length > 0) {
    parts.push(`${eventIds.length} event${eventIds.length === 1 ? "" : "s"}`);
  }

  if (dateFrom || dateTo) {
    parts.push(`${dateFrom || "…"} – ${dateTo || "…"}`);
  }

  return parts.join(" · ");
}
